import React from 'react'
import PropTypes from 'prop-types'
import useInstitutionType from '../../../../../hooks/useInstitutionType'
import useInstitution from '../../../../../hooks/useInstitution'
import useUbigeos from '../../../../../hooks/useUbigeos'
import useFieldValue from '../../../../../hooks/useFieldValue'
import useUserPermission from '../../../../../hooks/useUserPermission'
import useTemplate from '../../../../../hooks/services/useTemplate'
import useUser from '../../../../../hooks/services/useUser'
import { useAuth } from '../../../../molecules/Auth'
import useSearchParams from '../../../../../hooks/useSearchParams'
import useBranchOffices from '../../../../../hooks/useBranchOffice'
import ChipAppliedFilter from '../../../filters/ChipAppliedFilter'

const AppliedAttendedRequestFilter = ({ form }) => {
  const filterPrefix = 'attended_'
  const { user } = useAuth()
  const formValues = form.getState().values
  const appliedFilters = Object.entries(formValues).filter(([key]) => key.startsWith(filterPrefix))
  const { canSeeInstitutionFilter, canSeeBranchOfficeFilter } = useUserPermission('statistics')

  const { getPublishedTemplates } = useTemplate()
  const { data: templates } = getPublishedTemplates()

  const { getInstitutionTypes } = useInstitutionType()
  const { data: institutionTypes } = getInstitutionTypes({
    enabled: canSeeInstitutionFilter,
    initialData: []
  })

  const { getInstitutions } = useInstitution()
  const { data: institutions } = getInstitutions(undefined, {
    enabled: canSeeInstitutionFilter
  })

  const { getBranchOffices } = useBranchOffices(user.institution_id)
  const { data: branchOffices } = getBranchOffices({}, { enabled: canSeeBranchOfficeFilter })

  const { getCollaborator } = useUser()
  const searchParams = useSearchParams()
  const paramsInstitutionId = searchParams.get(`${filterPrefix}request_institution_id`)
  const { data: users } = getCollaborator({
    institutionId: canSeeInstitutionFilter ? paramsInstitutionId : user.institution_id
  })

  const regionId = useFieldValue(`${filterPrefix}region`)
  const provinceId = useFieldValue(`${filterPrefix}province`)
  const { getRegions, getProvinces, getDistricts } = useUbigeos()
  const { data: regions } = getRegions()
  const { data: provinces } = getProvinces(regionId)
  const { data: districts } = getDistricts(provinceId)

  const getNameFromList = (list, id) => list?.find(el => el.id.toString() === id)?.name

  const filterResolvers = {
    [`${filterPrefix}request_institution_type`]: v => `Tipo de entidad: ${getNameFromList(institutionTypes, v)}`,
    [`${filterPrefix}request_institution_id`]: v => `Entidad: ${getNameFromList(institutions, v)}`,
    [`${filterPrefix}request_municipality_type`]: v => `Subtipo de entidad: ${v}`,
    [`${filterPrefix}branch_office_id`]: v => `Sede: ${getNameFromList(branchOffices, v)}`,
    [`${filterPrefix}region`]: v => `Departamento: ${getNameFromList(regions, v)}`,
    [`${filterPrefix}province`]: v => `Provincia: ${getNameFromList(provinces, v)}`,
    [`${filterPrefix}district`]: v => `Distrito: ${getNameFromList(districts, v)}`,
    [`${filterPrefix}request_form_type`]: v =>
      v === 'simple'
        ? 'Tipo de formulario: Simple'
        : `Tipo de formulario: ${getNameFromList(templates, v.split('_')[1])}`,
    [`${filterPrefix}search_name`]: v => `Nombre o ID de formulario: ${v}`,
    [`${filterPrefix}collaborator`]: v => `Colaborador: ${getNameFromList(users, v)}`,
    [`${filterPrefix}date_from`]: v => `Desde: ${v}`,
    [`${filterPrefix}date_to`]: v => `Hasta: ${v}`
  }

  const getFilterName = (filterKey, value) => {
    const resolver = filterResolvers[filterKey]
    return resolver ? resolver(value) : null
  }

  if (appliedFilters.length === 0) return null

  return (
    <div className="flex flex-wrap items-center gap-2">
      <p className="font-medium text-gray-800">Filtros aplicados:</p>
      {appliedFilters.map(([filterKey, value]) => {
        if (!value) return null

        const filterName = getFilterName(filterKey, value)
        return (
          <ChipAppliedFilter
            key={`${filterKey}-${value}`}
            label={filterName}
            onDelete={() => form.change(filterKey, '')}
          />
        )
      })}
    </div>
  )
}

AppliedAttendedRequestFilter.propTypes = {
  form: PropTypes.object.isRequired
}

export default AppliedAttendedRequestFilter
